// AI-CONTEXT: See docs/features/msi-onderbouwing-transparency.md
// AI-CONTEXT: See docs/patterns.md#data-week-cached-external-fetch-hook
import { useEffect, useState } from 'react'
import buildOnderbouwingIndex from '../helpers/msiMonetisationHelpers/buildOnderbouwingIndex'

// Live source of the substantiation behind the MSI monetary values: per
// effect the methodiek, the financial opbouw per score level, the tariffs
// used and a central source registry. The document is versioned and immutable
// upstream; this hook always shows the latest version. Same fail-visibly +
// week-cache pattern as useMsiMonetaryScores — the onderbouwing explains the
// euro values, it never blocks or redefines them.
const ONDERBOUWING_URL =
    'https://us-central1-meetstandaard-api.cloudfunctions.net/monetarisering/api/v1/monetarisering/onderbouwing'

const CACHE_KEY = 'msiOnderbouwingCache'
const WEEK_MS = 7 * 24 * 60 * 60 * 1000

const loadCached = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY)
        if (!raw) return null
        return JSON.parse(raw)
    } catch (e) {
        console.warn('Failed to parse MSI onderbouwing cache', e)
        return null
    }
}

const saveCached = (data) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data, fetchedAt: Date.now() }))
    } catch (e) {
        console.warn('Failed to save MSI onderbouwing cache', e)
    }
}

// One built index per document per session: cache hits would otherwise
// re-parse and rebuild the index for every mounted ScoreBlock. Keyed on the
// cache stamp (warm path) or the shared fetch-result object (cold path —
// pendingFetch hands every waiter the same object).
let builtIndexCache = null

const getBuiltIndex = (doc, key) => {
    if (!builtIndexCache || builtIndexCache.key !== key) {
        builtIndexCache = { key, index: buildOnderbouwingIndex(doc) }
    }
    return builtIndexCache.index
}

// In-flight dedup: on a cold cache several ScoreBlocks mount at once — share
// one network fetch. Cleared afterwards so a failure can be retried;
// successful results are served from localStorage anyway.
let pendingFetch = null

const fetchDocumentShared = () => {
    if (!pendingFetch) {
        pendingFetch = (async () => {
            const res = await fetch(ONDERBOUWING_URL)
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const doc = await res.json()
            saveCached(doc)
            return doc
        })().finally(() => { pendingFetch = null })
    }
    return pendingFetch
}

/**
 * Fetches the meetstandaard onderbouwing document (latest version) and
 * exposes it as a lookup index, caching the raw document in localStorage for
 * a week. On failure it surfaces the error and returns no index (fail
 * visibly — consumers hide the onderbouwing affordance and keep showing the
 * euro values themselves).
 *
 * @param {boolean} [enabled] - Pass false to skip fetching entirely (public
 *   cards render no onderbouwing, so they must not pay for the payload)
 * @returns {{
 *   index: import('../helpers/msiMonetisationHelpers/buildOnderbouwingIndex').OnderbouwingIndex | null,
 *   meta: object | null,
 *   loading: boolean,
 *   error: Error | null,
 * }}
 */
const useMsiOnderbouwing = (enabled = true) => {
    const [index, setIndex] = useState(/** @type {object | null} */ (null))
    const [loading, setLoading] = useState(enabled)
    const [error, setError] = useState(/** @type {Error | null} */ (null))

    useEffect(() => {
        if (!enabled) {
            setIndex(null)
            setLoading(false)
            setError(null)
            return
        }

        const cached = loadCached()
        if (cached && Date.now() - cached.fetchedAt < WEEK_MS && cached.data) {
            setIndex(getBuiltIndex(cached.data, cached.fetchedAt))
            setLoading(false)
            return
        }

        let cancelled = false

        const fetchDocument = async () => {
            try {
                const doc = await fetchDocumentShared()
                if (cancelled) return

                setIndex(getBuiltIndex(doc, doc))
                setError(null)
                setLoading(false)
            } catch (err) {
                console.error('Error fetching MSI onderbouwing', err)
                if (cancelled) return

                setError(err)
                setIndex(null)
                setLoading(false)
            }
        }

        setLoading(true)
        fetchDocument()

        return () => {
            cancelled = true
        }
    }, [enabled])

    return { index, meta: index?.meta || null, loading, error }
}

export default useMsiOnderbouwing
